import _object_spread from "@swc/helpers/src/_object_spread.mjs";
import _object_spread_props from "@swc/helpers/src/_object_spread_props.mjs";
import _object_without_properties from "@swc/helpers/src/_object_without_properties.mjs";
import axios from 'axios';
import throttle from 'lodash/throttle';
import { all, takeEvery, take, call, put, select, actionChannel } from 'redux-saga/effects';
import { buffers, eventChannel, END } from 'redux-saga';
import { Url, URLS } from '@wix/da-url';
import { ConfigSelectors } from '../../config';
import { getCsrfToken } from '../../publicSession/selectors';
import { uploadFile, uploadFailure, uploadSuccess, uploadProgress, uploadFiles, uploadFileImmediately } from './actions';
import { getUploads, getUploadsWaiting, getUploadById } from './selectors';
import { isApiError, request } from '../../xhr';
const THROTTLE_WAIT = 500;
const MAXIMUM_CONCURRENT_UPLOADS = 10;
const DAPI_ROOT = Url.buildFromPath('dapi/v1', URLS.ROOT);
const fakeDeviationFetch = (options)=>{
    if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line
        const { deviationsByType  } = require('@wix/da-sample-data');
        return _object_spread({}, deviationsByType.image[0], {
            deviationId: options.params.deviationid
        });
    }
};
// If you are hopelessly trying to find out why uploads do not work on dev node
// It's because dev node runs on http and dapi is https. Just give up and test on staging
async function dapiPost(endpoint, data, options) {
    const { onSuccess , onError  } = options, axiosOptions = _object_without_properties(options, [
        "onSuccess",
        "onError"
    ]);
    try {
        const response = await axios.request(_object_spread_props(_object_spread({
            method: 'post',
            baseURL: DAPI_ROOT,
            url: `/${endpoint}`
        }, axiosOptions), {
            data
        }));
        if (response.data.error) {
            onError && onError(_object_spread_props(_object_spread({}, response.data), {
                isServerError: true
            }));
            return false;
        }
        onSuccess && onSuccess(response);
        return response.data;
    } catch (error) {
        onError && onError(error);
        return false;
    }
}
function createUploadFileChannel(params) {
    return eventChannel((emitter)=>{
        const source = axios.CancelToken.source();
        const throttledEmitter = throttle(emitter, THROTTLE_WAIT);
        dapiPost('submit/upload', params, {
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            withCredentials: true,
            cancelToken: source.token,
            onUploadProgress: (e)=>{
                if (e.lengthComputable) {
                    const progress = e.loaded / e.total;
                    throttledEmitter({
                        progress
                    });
                }
            },
            onSuccess: (response)=>{
                emitter({
                    success: true,
                    response
                });
            },
            onError: (err)=>{
                var _err_error_description;
                const error = new Error((_err_error_description = err.error_description) !== null && _err_error_description !== void 0 ? _err_error_description : 'Upload failed');
                if (err.isServerError) {
                    error['isServerError'] = true;
                }
                emitter({
                    error
                });
                emitter(END);
            }
        });
        return ()=>{
            source.cancel();
        };
    }, buffers.sliding(2));
}
let fakeDeviationId = 123;
// Simulate file upload channel events
function createFakeUploadFileChannel(params) {
    return eventChannel((emitter)=>{
        const throttledEmitter = throttle(emitter, THROTTLE_WAIT);
        let progress = 0;
        const interval = setInterval(()=>{
            progress = Math.min(1, progress);
            throttledEmitter({
                progress
            });
            if (progress < 1) {
                return progress += Math.random() / 100;
            }
            if (window.doError) {
                emitter({
                    error: new Error('Test error')
                });
                emitter(END);
                return;
            }
            const response = {
                data: {
                    content: {
                        privateid: fakeDeviationId++
                    }
                }
            };
            emitter({
                progress: 1
            });
            emitter({
                success: true,
                response
            });
            emitter(END);
        }, 50);
        return ()=>{
            clearInterval(interval);
        };
    });
}
function* createReduxChannel({ file , folderid , folderName , isNewSubmit  }) {
    const formData = new FormData();
    formData.append('file', file, file.name);
    folderid && formData.append('folderid', folderid);
    // if no folderid is specified, the folder name must be set to "Saved Submissions" as per endpoint spec
    // except stash protect which is a special peanut
    !folderid && formData.append('folder_name', folderName !== null && folderName !== void 0 ? folderName : 'Saved Submissions');
    // we only support uploading to stash
    formData.append('action', 'StashPost');
    if (isNewSubmit) {
        formData.append('is_new_dapi_submit', 'true');
    }
    formData.append('_csrf', (yield select(getCsrfToken)));
    if (process.env.NODE_ENV !== 'production') {
        const doFake = yield select(ConfigSelectors.key('fakeApiRequests'));
        if (doFake) {
            return createFakeUploadFileChannel(formData);
        }
    }
    return createUploadFileChannel(formData);
}
function* uploadFileHandler(action) {
    const { file , fileId , folderId , folderName , autoFetchDeviationOnSuccess , isNewSubmit , onSuccess , onFailure  } = action.payload;
    const channel = yield call(createReduxChannel, {
        file,
        folderid: folderId,
        folderName,
        isNewSubmit
    });
    while(true){
        const fileUpload = yield select(getUploadById, fileId);
        if (!fileUpload) {
            // if the file was removed from store (i.e. cancelled) just bail
            return;
        }
        const { progress =0 , error , success , response  } = yield take(channel);
        if (error) {
            yield put(uploadFailure(fileId, error));
            if (onFailure) {
                yield call(onFailure, {
                    fileId
                });
            }
            return;
        }
        if (success) {
            yield put(uploadSuccess(fileId));
            if (onSuccess) {
                var _response_data, _response_data_content;
                let deviation;
                const deviationId = response === null || response === void 0 ? void 0 : (_response_data = response.data) === null || _response_data === void 0 ? void 0 : (_response_data_content = _response_data.content) === null || _response_data_content === void 0 ? void 0 : _response_data_content.privateid;
                if (autoFetchDeviationOnSuccess && deviationId) {
                    const params = {
                        deviationid: deviationId,
                        type: 'art'
                    };
                    const result = yield call(request, {
                        method: 'get',
                        url: '/deviation/fetch',
                        params: params
                    }, fakeDeviationFetch);
                    if (result && !isApiError(result)) {
                        deviation = result;
                    }
                }
                yield call(onSuccess, {
                    fileId,
                    file,
                    deviationId,
                    deviation
                });
            }
            return;
        }
        yield put(uploadProgress(fileId, progress));
    }
}
function* uploadQueueHandler(action) {
    const uploads = yield select(getUploads);
    const activeUploads = uploads.filter((upload)=>!upload.error);
    const openUploadSlots = MAXIMUM_CONCURRENT_UPLOADS - activeUploads.length;
    if (openUploadSlots <= 0) {
        // do nothing, we have no space to queue more
        return;
    }
    const waitingUploads = yield select(getUploadsWaiting);
    // start downloading the appropriate amount
    yield all(waitingUploads.slice(0, openUploadSlots).map((upload)=>put(uploadFileImmediately(upload))));
}
function* uploadQueueWatcher() {
    const channel = yield actionChannel([
        uploadFile,
        uploadFiles,
        uploadSuccess,
        uploadFailure
    ]);
    while(true){
        const action = yield take(channel);
        yield call(uploadQueueHandler, action);
    }
}
export default function* saga() {
    yield all([
        takeEvery(uploadFileImmediately, uploadFileHandler),
        // update the queue every time something relevant happens
        uploadQueueWatcher()
    ]);
}
